import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Layout from '../../Layout/Layout';
import styles from './Logs.module.css';
import { URL as BASEURL } from '../../AxiosUrlGlobal';

const Logs = () => {
  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [limit, setLimit] = useState(50);

  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [dateFilter, setDateFilter] = useState('today');
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  const userId = localStorage.getItem('id');
  const token = localStorage.getItem('token');

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearch, selectedCategory, dateFilter, limit]);

  useEffect(() => {
    fetchLogs();
    fetchStats();
  }, [currentPage, debouncedSearch, selectedCategory, dateFilter, limit]);

  const fetchLogs = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${BASEURL}superadmin/logs/${userId}`,
        {
          params: {
            page: currentPage,
            limit,
            category: selectedCategory,
            search: debouncedSearch,
            dateFilter
          },
          headers: { token }
        }
      );

      if (response.data.success) {
        setLogs(response.data.logs);
        setTotal(response.data.pagination.total);
        setTotalPages(response.data.pagination.totalPages);
        setCategories(response.data.categories);
        setError(null);
      } else {
        setError(response.data.error || 'Failed to fetch logs');
      }
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to fetch logs');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get(
        `${BASEURL}superadmin/log-stats/${userId}`,
        { headers: { token } }
      );
      if (response.data.success) {
        setStats(response.data.stats);
      }
    } catch (err) {
      console.error('Failed to fetch stats:', err);
    }
  };

  const handleClearLogs = async (action, category = null) => {
    const confirmMessages = {
      clearCache: 'Clear error deduplication cache?',
      cleanupOld: 'Delete logs older than 2 days?',
      deleteCategory: `Delete all logs for category "${category}"?`,
      deleteAll: 'Delete ALL log files? This cannot be undone!'
    };

    if (!window.confirm(confirmMessages[action])) return;

    try {
      const response = await axios.post(
        `${BASEURL}superadmin/logs/clear/${userId}`,
        { action, category },
        { headers: { token } }
      );

      if (response.data.success) {
        alert(response.data.message);
        fetchLogs();
        fetchStats();
      } else {
        alert(response.data.error || 'Failed to clear logs');
      }
    } catch (err) {
      alert(err.response?.data?.error || err.message || 'Failed to clear logs');
    }
  };

  const getLevelBadgeClass = (level) => {
    switch (level.toUpperCase()) {
      case 'ERROR': return styles.levelError;
      case 'WARN': return styles.levelWarn;
      case 'INFO': return styles.levelInfo;
      default: return styles.levelInfo;
    }
  };

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false
    });
  };

  if (loading && !logs.length) {
    return (
      <Layout>
        <div className={styles.loading}>
          <i className="fas fa-spinner fa-spin"></i>
          <span>Loading logs...</span>
        </div>
      </Layout>
    );
  }

  if (error && !logs.length) {
    return (
      <Layout>
        <div className={styles.error}>
          <i className="fas fa-exclamation-circle"></i>
          {error}
        </div>
      </Layout>
    );
  }

  const start = (currentPage - 1) * limit + 1;
  const end = Math.min(currentPage * limit, total);

  return (
    <Layout>
      <div className={styles.container}>
        {/* Header */}
        <div className={styles.header}>
          <div className={styles.headerContent}>
            <h1>System Logs</h1>
            <p>Monitor and manage platform error logs with automatic deduplication</p>
          </div>
          {stats && (
            <div className={styles.stats}>
              <div className={styles.statItem}>
                <strong>{stats.totalFiles}</strong>
                <span>LOG FILES</span>
              </div>
              <div className={styles.statItem}>
                <strong>{stats.totalSize} MB</strong>
                <span>TOTAL SIZE</span>
              </div>
              <div className={styles.statItem}>
                <strong>{stats.levels.ERROR}</strong>
                <span>ERRORS</span>
              </div>
              <div className={styles.statItem}>
                <strong>{stats.levels.WARN}</strong>
                <span>WARNINGS</span>
              </div>
            </div>
          )}
        </div>

        {/* Actions */}
        <div className={styles.actionsBar}>
          <button
            onClick={() => handleClearLogs('clearCache')}
            className={styles.actionBtn}
          >
            <i className="fas fa-sync"></i> Clear Cache
          </button>
          <button
            onClick={() => handleClearLogs('cleanupOld')}
            className={styles.actionBtn}
          >
            <i className="fas fa-broom"></i> Cleanup Old
          </button>
          {selectedCategory !== 'all' && (
            <button
              onClick={() => handleClearLogs('deleteCategory', selectedCategory)}
              className={styles.actionBtnDanger}
            >
              <i className="fas fa-trash"></i> Delete {selectedCategory}
            </button>
          )}
          <button
            onClick={() => handleClearLogs('deleteAll')}
            className={styles.actionBtnDanger}
          >
            <i className="fas fa-trash-alt"></i> Delete All Logs
          </button>
        </div>

        {/* Filters */}
        <div className={styles.filtersSection}>
          <div className={styles.filtersRow}>
            <div className={styles.searchBox}>
              <i className="fas fa-search"></i>
              <input
                type="text"
                placeholder="Search logs..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>

            <select
              value={dateFilter}
              onChange={(e) => setDateFilter(e.target.value)}
              className={styles.selectFilter}
            >
              <option value="today">Today</option>
              <option value="yesterday">Yesterday</option>
              <option value="all">All Dates</option>
            </select>

            <select
              value={limit}
              onChange={(e) => setLimit(Number(e.target.value))}
              className={styles.selectFilter}
            >
              <option value="50">50 per page</option>
              <option value="100">100 per page</option>
              <option value="200">200 per page</option>
            </select>
          </div>

          <div className={styles.categoryFilters}>
            <button
              className={selectedCategory === 'all' ? styles.active : ''}
              onClick={() => setSelectedCategory('all')}
            >
              All
            </button>
            {categories.map(cat => (
              <button
                key={cat}
                className={selectedCategory === cat ? styles.active : ''}
                onClick={() => setSelectedCategory(cat)}
              >
                {cat}
              </button>
            ))}
          </div>
        </div>

        {/* Logs Table */}
        <div className={styles.tableSection}>
          <div className={styles.tableWrapper}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th style={{ width: '140px' }}>Timestamp</th>
                  <th style={{ width: '80px' }}>Level</th>
                  <th style={{ width: '100px' }}>Category</th>
                  <th>Message</th>
                  <th style={{ width: '120px' }}>File</th>
                </tr>
              </thead>
              <tbody>
                {logs.length === 0 ? (
                  <tr>
                    <td colSpan="5" className={styles.emptyState}>
                      <i className="fas fa-inbox"></i>
                      <p>No logs found</p>
                    </td>
                  </tr>
                ) : (
                  logs.map((log, index) => (
                    <tr key={index}>
                      <td className={styles.timestamp}>
                        {formatTimestamp(log.timestamp)}
                      </td>
                      <td>
                        <span className={`${styles.levelBadge} ${getLevelBadgeClass(log.level)}`}>
                          {log.level}
                        </span>
                      </td>
                      <td className={styles.category}>{log.category}</td>
                      <td className={styles.message}>
                        <div className={styles.messageContent}>{log.message}</div>
                        {log.metadata && Object.keys(log.metadata).length > 0 && (
                          <div className={styles.metadata}>
                            <code>{JSON.stringify(log.metadata, null, 2)}</code>
                          </div>
                        )}
                      </td>
                      <td className={styles.filename}>{log.filename}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className={styles.pagination}>
            <div className={styles.paginationInfo}>
              <p>Showing {start} to {end} of {total} logs</p>
            </div>
            <div className={styles.paginationControls}>
              <button
                onClick={() => setCurrentPage(1)}
                disabled={currentPage === 1}
                className={styles.paginationBtn}
              >
                <i className="fas fa-angle-double-left"></i>
              </button>
              <button
                onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
                className={styles.paginationBtn}
              >
                <i className="fas fa-angle-left"></i>
              </button>

              {[...Array(Math.min(5, totalPages))].map((_, i) => {
                let pageNum;
                if (totalPages <= 5) {
                  pageNum = i + 1;
                } else if (currentPage <= 3) {
                  pageNum = i + 1;
                } else if (currentPage >= totalPages - 2) {
                  pageNum = totalPages - 4 + i;
                } else {
                  pageNum = currentPage - 2 + i;
                }

                return (
                  <button
                    key={i}
                    onClick={() => setCurrentPage(pageNum)}
                    className={`${styles.paginationBtn} ${currentPage === pageNum ? styles.active : ''}`}
                  >
                    {pageNum}
                  </button>
                );
              })}

              <button
                onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                disabled={currentPage === totalPages}
                className={styles.paginationBtn}
              >
                <i className="fas fa-angle-right"></i>
              </button>
              <button
                onClick={() => setCurrentPage(totalPages)}
                disabled={currentPage === totalPages}
                className={styles.paginationBtn}
              >
                <i className="fas fa-angle-double-right"></i>
              </button>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default Logs;
